// Kleine Bausteine: Avatar, Status-Pille, Zeitangabe, Ladeanzeige, Leerzustand, Kopierknopf.
import { useEffect, useState, type ReactNode } from "react";
import { statusFarbe, wirksameStatusFarbe, scoreStufe, type ScoreStufe } from "../werkzeuge/statusFarben";
import { kopieren } from "../werkzeuge/tasten";
import { relativ, zeitpunktLang, zeitpunktText } from "../werkzeuge/zeit";
import { Icon } from "./Icon";
import { useMeldung } from "./Meldungen";

const AVATAR_FARBEN = ["blue", "green", "orange", "purple", "teal", "magenta", "yellow"];

export function initialen(name: string | null | undefined): string {
  const teile = (name ?? "").replace(/[^\p{L}\p{N}\s-]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (!teile.length) return "?";
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase();
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
}

function farbeAus(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_FARBEN[h % AVATAR_FARBEN.length];
}

export function Avatar({ name, id, groesse = 22, titel }: { name: string | null | undefined; id?: string | null; groesse?: number; titel?: string }) {
  return (
    <span
      className={`avatar avatar--${farbeAus(id ?? name ?? "")}`}
      style={{ width: groesse, height: groesse, fontSize: Math.max(9, Math.round(groesse * 0.42)) }}
      title={titel ?? name ?? undefined}
      aria-hidden={titel ? undefined : true}
    >
      {initialen(name)}
    </span>
  );
}

export { statusFarbe };

const SCORE_TEXT: Record<ScoreStufe, string> = { bronze: "Bronze", silber: "Silber", gold: "Gold" };

/** Lead Score als Abzeichen: Rahmen und Symbol in der Metallfarbe, Text in ruhiger Schrift. Kein Treffer: nur der Text. */
export function ScoreAbzeichen({ wert, klein = false }: { wert: unknown; klein?: boolean }) {
  const stufe = scoreStufe(wert);
  if (!stufe) return null;
  return (
    <span className={`score score--${stufe}${klein ? " score--klein" : ""}`} title={`Lead Score: ${SCORE_TEXT[stufe]}`}>
      <svg className="score__symbol" width={klein ? 11 : 13} height={klein ? 11 : 13} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8.2 14.6 6 22l6-3.4 6 3.4-2.2-7.4a7.5 7.5 0 0 1-7.6 0Z" opacity="0.55" />
        <circle cx="12" cy="9" r="6.5" />
        <path d="m12 5.6 1.1 2.3 2.5.3-1.8 1.7.5 2.5-2.3-1.3-2.3 1.3.5-2.5-1.8-1.7 2.5-.3Z" fill="var(--score-stern)" />
      </svg>
      <span>{SCORE_TEXT[stufe]}</span>
    </span>
  );
}

/** `schlicht` (Listen): Text mit kleinem Farbpunkt, ohne Rahmen und Hintergrund. Die Pille bleibt auf der Lead-Seite. */
export function StatusPille({ label, farbe, klein = false, schlicht = false }: { label: string | null | undefined; farbe?: string | null; klein?: boolean; schlicht?: boolean }) {
  if (!label) return null;
  return (
    <span className={`status-pille status--${statusFarbe(wirksameStatusFarbe(label, farbe))}${klein ? " status-pille--klein" : ""}${schlicht ? " status-pille--schlicht" : ""}`} title={label}>
      <span className="status-pille__punkt" />
      <span className="status-pille__text">{label}</span>
    </span>
  );
}

// Ein Takt für alle relativen Zeitangaben (jede Minute neu zeichnen).
const hoerer = new Set<() => void>();
let takt: number | undefined;
function useMinutenTakt(): void {
  const [, setN] = useState(0);
  useEffect(() => {
    const f = () => setN((n) => n + 1);
    hoerer.add(f);
    if (takt === undefined) takt = window.setInterval(() => hoerer.forEach((h) => h()), 60_000);
    return () => {
      hoerer.delete(f);
      if (!hoerer.size && takt !== undefined) {
        window.clearInterval(takt);
        takt = undefined;
      }
    };
  }, []);
}

/** Zeit relativ („vor 5 Min.“) und absolut („13.10.2026, 14:05“). */
export function Zeit({ iso, nurRelativ = false }: { iso: string | null | undefined; nurRelativ?: boolean }) {
  useMinutenTakt();
  if (!iso) return null;
  return (
    <time className="zeit" dateTime={iso} title={zeitpunktLang(iso)}>
      <span className="zeit__relativ">{relativ(iso)}</span>
      {nurRelativ ? null : <span className="zeit__absolut">{zeitpunktText(iso)}</span>}
    </time>
  );
}

/** Ruhiges Laden mit Platzhaltern (Design-Runde 2, N): graue Balken statt Kreisel, erst nach einem Atemzug sichtbar. */
export function Laden({ text = "Wird geladen …", klein = false }: { text?: string; klein?: boolean }) {
  const breiten = klein ? [72, 54] : [38, 92, 78, 86, 60];
  return (
    <div className={`laden-anzeige laden-anzeige--skel${klein ? " laden-anzeige--klein" : ""}`} role="status" aria-label={text}>
      {breiten.map((b, i) => (
        <span key={i} className={`skel skel--zeile${i === 0 && !klein ? " skel--kopf" : ""}`} style={{ width: `${b}%` }} />
      ))}
      <span className="nur-vorlesen">{text}</span>
    </div>
  );
}

export function Leer({ titel, text, children }: { titel: string; text?: string; children?: ReactNode }) {
  return (
    <div className="leer">
      <div className="leer__titel">{titel}</div>
      {text ? <div className="leer__text">{text}</div> : null}
      {children}
    </div>
  );
}

export function KopierKnopf({ text, label = "Kopieren" }: { text: string; label?: string }) {
  const meldung = useMeldung();
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      className="symbolknopf symbolknopf--klein"
      title={`${label}: ${text}`}
      aria-label={`${label}: ${text}`}
      onClick={async (e) => {
        e.stopPropagation();
        if (await kopieren(text)) {
          setOk(true);
          window.setTimeout(() => setOk(false), 1200);
        } else {
          meldung.zeige("fehler", "Kopieren hat nicht geklappt.");
        }
      }}
    >
      <Icon name={ok ? "haken" : "kopieren"} groesse={14} />
    </button>
  );
}
